// Strains + Ingredients drawer content; each empty strains module field falls back to the code defaults.
import styled from 'styled-components';
import Image from 'next/image';
import Accordion from 'components/library/Accordion/accordion';
import { imageUrl, richTextToHtml } from 'lib/pdp-modules/helpers';
import type {
  RichText,
  StrainsBlend,
  StrainsModule,
  TransformativeIcon,
} from 'lib/pdp-modules/types';
import {
  ALT_OUTER_CAPSULE_SUPPORTING,
  ALT_STRAINS_SECTION_HERO,
  ALT_SUPPLEMENT_FACTS_PANEL,
  DEFAULT_OTHER_INGREDIENTS_ITEMS,
  DEFAULT_STRAIN_BLEND_ITEMS,
  FALLBACK_INNER_CAPSULE_DESCRIPTION,
  FALLBACK_INNER_CAPSULE_TITLE,
  FALLBACK_INTRO_TEXT,
  FALLBACK_MAIN_TITLE,
  FALLBACK_OTHER_INGREDIENTS_TITLE,
  FALLBACK_OUTER_CAPSULE_DESCRIPTION,
  FALLBACK_OUTER_CAPSULE_TITLE,
  FALLBACK_SAFETY_DESCRIPTION,
  FALLBACK_SAFETY_HEADING,
  FALLBACK_STRAINS_HEADING,
  FALLBACK_SUBTITLE,
  FALLBACK_SUPPLEMENT_FACTS_HEADING,
  type StrainBlendAccordionItem,
} from 'components/pdp/ds01/ds-strains-defaults';

const StrainsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  .accordion-title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .accordion-title span {
    font-family: 'Source Code Pro', 'Fira Code', monospace;
    font-size: 14px;
    color: var(--color-neutral-faded-green-60);
  }

  svg {
    min-width: 16px;
  }
`;

const MainTitle = styled.p`
  margin-bottom: 0.5rem;
  font-size: 20px;
  font-weight: 400;
  line-height: 32px;
`;

const TitleDashed = styled.p`
  margin-top: 1.5rem;
  border-top: 1px dashed var(--fill-brand);
  padding-top: 1.5rem;
  font-size: 18px;
  line-height: 25.2px;
  font-weight: 500;
`;

const Bolded = styled.b`
  display: inline-block;
  margin: 1.5rem 0 0.5rem;
`;

const AccordionWrapper = styled.div`
  margin-top: 1.5rem;
`;

const StrainsMetafieldImageFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
`;

const IconGrid = styled.div`
  margin-top: 1.5rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 1rem;
  column-gap: 0.75rem;
`;

const BenefitIconImageFrame = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
`;

const richTextFieldToHtml = (value?: RichText): string | null =>
  richTextToHtml(value, { newLineToBreak: true });

const buildStrainBlendAccordionItems = (
  blends?: StrainsBlend[],
): StrainBlendAccordionItem[] => {
  if (!blends?.length) {
    return DEFAULT_STRAIN_BLEND_ITEMS;
  }

  return blends.map((blend) => {
    return {
      title: `<span class="accordion-title">${blend.title ?? ''} <span>${blend.afuCount ?? ''}</span></span>`,
      content: richTextFieldToHtml(blend.content) ?? '',
    };
  });
};

interface DSStrainsProps {
  strainsModule?: StrainsModule;
  icons?: TransformativeIcon[];
}

const DSStrains = ({ strainsModule, icons }: DSStrainsProps) => {
  const lineFromStrainsModule = (value: string | undefined, fallback: string) =>
    value?.trim() || fallback;

  const heroImageUrl = strainsModule?.heroImageUrl;
  const outerCapsuleImageUrl = strainsModule?.outerCapsuleImageUrl;
  const supplementFactsImageUrl = strainsModule?.supplementFactsImageUrl;

  const mainTitle = lineFromStrainsModule(
    strainsModule?.title,
    FALLBACK_MAIN_TITLE,
  );
  const subtitle = lineFromStrainsModule(
    strainsModule?.subtitle,
    FALLBACK_SUBTITLE,
  );
  const strainsHeading = lineFromStrainsModule(
    strainsModule?.strainsHeading,
    FALLBACK_STRAINS_HEADING,
  );
  const innerCapsuleTitle = lineFromStrainsModule(
    strainsModule?.innerCapsuleTitle,
    FALLBACK_INNER_CAPSULE_TITLE,
  );
  const outerCapsuleTitle = lineFromStrainsModule(
    strainsModule?.outerCapsuleTitle,
    FALLBACK_OUTER_CAPSULE_TITLE,
  );
  const otherIngredientsTitle = lineFromStrainsModule(
    strainsModule?.otherIngredientsTitle,
    FALLBACK_OTHER_INGREDIENTS_TITLE,
  );
  const safetyHeading = lineFromStrainsModule(
    strainsModule?.safetyHeading,
    FALLBACK_SAFETY_HEADING,
  );
  const supplementFactsHeading = lineFromStrainsModule(
    strainsModule?.supplementFactsHeading,
    FALLBACK_SUPPLEMENT_FACTS_HEADING,
  );

  const introHtml = richTextFieldToHtml(strainsModule?.introText);
  const innerCapsuleDescHtml = richTextFieldToHtml(
    strainsModule?.innerCapsuleDescription,
  );
  const outerCapsuleDescHtml = richTextFieldToHtml(
    strainsModule?.outerCapsuleDescription,
  );
  const otherIngredientsContentHtml = richTextFieldToHtml(
    strainsModule?.otherIngredientsContent,
  );
  const safetyDescHtml = richTextFieldToHtml(strainsModule?.safetyDescription);

  const strainBlendItems = buildStrainBlendAccordionItems(
    strainsModule?.blends,
  );

  const otherIngredientsItems =
    otherIngredientsContentHtml !== null
      ? [
          {
            title: otherIngredientsTitle,
            content: otherIngredientsContentHtml,
          },
        ]
      : DEFAULT_OTHER_INGREDIENTS_ITEMS;

  return (
    <StrainsWrapper>
      <MainTitle>{mainTitle}</MainTitle>
      {introHtml ? (
        <div
          dangerouslySetInnerHTML={{
            __html: introHtml,
          }}
        />
      ) : (
        <p>{FALLBACK_INTRO_TEXT}</p>
      )}
      <p>{subtitle}</p>

      <TitleDashed>{strainsHeading}</TitleDashed>
      {heroImageUrl && (
        <StrainsMetafieldImageFrame>
          <Image
            src={heroImageUrl}
            alt={ALT_STRAINS_SECTION_HERO}
            fill
            sizes='100vw'
            style={{ objectFit: 'contain' }}
          />
        </StrainsMetafieldImageFrame>
      )}
      <Bolded>{innerCapsuleTitle}</Bolded>
      {innerCapsuleDescHtml ? (
        <div
          dangerouslySetInnerHTML={{
            __html: innerCapsuleDescHtml,
          }}
        />
      ) : (
        <p>{FALLBACK_INNER_CAPSULE_DESCRIPTION}</p>
      )}

      <AccordionWrapper>
        <Accordion
          displayTitleAsHTML={true}
          displayContentAsHTML={true}
          initialItem={-1}
          items={strainBlendItems}
        />
      </AccordionWrapper>

      <br />
      {outerCapsuleImageUrl && (
        <StrainsMetafieldImageFrame>
          <Image
            src={outerCapsuleImageUrl}
            alt={ALT_OUTER_CAPSULE_SUPPORTING}
            fill
            sizes='100vw'
            style={{ objectFit: 'contain' }}
          />
        </StrainsMetafieldImageFrame>
      )}

      <Bolded>{outerCapsuleTitle}</Bolded>
      {outerCapsuleDescHtml ? (
        <div
          dangerouslySetInnerHTML={{
            __html: outerCapsuleDescHtml,
          }}
        />
      ) : (
        <p>{FALLBACK_OUTER_CAPSULE_DESCRIPTION}</p>
      )}
      <br />
      <Accordion
        initialItem={-1}
        displayContentAsHTML={true}
        items={otherIngredientsItems}
      />

      <TitleDashed>{safetyHeading}</TitleDashed>
      {safetyDescHtml ? (
        <div
          dangerouslySetInnerHTML={{
            __html: safetyDescHtml,
          }}
        />
      ) : (
        <p>{FALLBACK_SAFETY_DESCRIPTION}</p>
      )}

      <IconGrid>
        {icons?.map((icon) => {
          const src = imageUrl(icon.icon, 'thumbnail');
          if (!src) {
            return null;
          }
          return (
            <IconWrapper key={icon._key}>
              <BenefitIconImageFrame>
                <Image
                  src={src}
                  alt={icon.icon?.alt ?? ''}
                  fill
                  sizes='40px'
                  style={{ objectFit: 'contain' }}
                />
              </BenefitIconImageFrame>
              {icon.title}
            </IconWrapper>
          );
        })}
      </IconGrid>

      <TitleDashed>{supplementFactsHeading}</TitleDashed>
      {supplementFactsImageUrl && (
        <StrainsMetafieldImageFrame>
          <Image
            src={supplementFactsImageUrl}
            alt={ALT_SUPPLEMENT_FACTS_PANEL}
            fill
            sizes='100vw'
            style={{ objectFit: 'contain' }}
          />
        </StrainsMetafieldImageFrame>
      )}
    </StrainsWrapper>
  );
};

export default DSStrains;
